import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, HolidayCalendarChange } from '../utils/transactionStore';
const MakerHolidayCalendarTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pending, setPending] = useState<HolidayCalendarChange[]>([]);
  const [rejected, setRejected] = useState<HolidayCalendarChange[]>([]);
  const [selected, setSelected] = useState<HolidayCalendarChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setPending(transactionStore.getPendingHolidayCalendarChanges());
    setRejected(transactionStore.getRejectedHolidayCalendarChanges());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: HolidayCalendarChange, action: 'resubmit' | 'discard') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitHolidayCalendarChange(selected.id, reason);
        showToast(`Holiday request resubmitted for "${selected.name}"`, 'success');
      } else {
        transactionStore.discardHolidayCalendarChange(selected.id, reason);
        showToast(`Holiday request discarded for "${selected.name}"`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const pendingColumns: Column<HolidayCalendarChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1374" data-mp="4msu9lc9x5j5">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700
    }} data-id="element-1375" data-mp="eef4pfgsf0ku" />
  }, {
    id: 'name',
    label: 'Holiday Name',
    renderCell: row => <Typography variant="body2" fontWeight={700} noWrap sx={{
      maxWidth: 200
    }} data-id="element-1376" data-mp="scdbnzss3uqb">{row.name}</Typography>
  }, {
    id: 'date',
    label: 'Date',
    renderCell: row => <Typography variant="body2" fontWeight={600} data-id="element-1377" data-mp="2u4rsevjl6t55">{row.date}</Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1378" data-mp="2q4hpjrr3ciui" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1379" data-mp="2jztd6wo5ehp7">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  const rejectedColumns: Column<HolidayCalendarChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1380" data-mp="1wah3lvows7md">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700
    }} data-id="element-1381" data-mp="2188xgv9463t7" />
  }, {
    id: 'name',
    label: 'Holiday Name',
    renderCell: row => <Typography variant="body2" fontWeight={700} data-id="element-1382" data-mp="1s95a3f2wz28w">{row.name}</Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1383" data-mp="3hpmxt8x2bsl9">{row.checkerComment}</Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1384" data-mp="1mjm5p6fplxd6">{new Date(row.checkerActedAt || '').toLocaleString()}</Typography>
  }];
  const rejectedRowActions: RowAction<HolidayCalendarChange>[] = [{
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Delete',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1385" data-mp="ywdy2hut6c7z">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1386" data-mp="yvui36opxxwl">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1387" data-mp="tx1319ts9yub">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1389" data-mp="tuygx0vr4puv"><Typography variant="body2" fontWeight={500} data-id="element-1390" data-mp="oyzj6moj9vhs">Pending</Typography><Chip label={pending.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1391" data-mp="kwcx72ejn4si" /></Box>} data-id="element-1388" data-mp="txavd42awi36" />
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1393" data-mp="fzcr4rhp221e"><Typography variant="body2" fontWeight={500} data-id="element-1394" data-mp="2rjk57w3sz9vs">Rejected</Typography><Chip label={rejected.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1395" data-mp="2wjg8ft4oexaa" /></Box>} data-id="element-1392" data-mp="fy1oiuzzj0bv" />
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Holiday Calendar Requests" data={pending} columns={pendingColumns} keyField="id" emptyStateMessage="No pending holiday calendar requests" initialVisibleColumns={['action', 'name', 'date', 'status', 'requestedAt']} data-id="element-1396" data-mp="35kmq45j2ae54" /> : <DataTable title="Rejected Holiday Calendar Requests" data={rejected} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected holiday calendar requests" initialVisibleColumns={['action', 'name', 'checkerComment', 'checkerActedAt']} data-id="element-1397" data-mp="2ctmlbscwp6ce" />}

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1398" data-mp="1p420t513u110">
        <DialogTitle data-id="element-1399" data-mp="2xg58bbzse8b1">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1400" data-mp="2xflm3j33sxr7">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1401" data-mp="2xdsjcevm2xoh" />
            <Typography variant="h6" fontWeight={600} data-id="element-1402" data-mp="367xehahs2v9t">{actionType === 'resubmit' ? 'Resubmit' : 'Delete'} Holiday Request</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1403" data-mp="2eghiipubzw6x">
          <Box sx={{
          mt: 1
        }} data-id="element-1404" data-mp="2ei2fsn815t8h">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1405" data-mp="29iy6lwl3vvi5">
                  {selected.action} &middot; <strong data-id="element-1406" data-mp="2nj0lrb214atp">{selected.name}</strong> &middot; {selected.date}
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1407" data-mp="2ii32sw973cr5">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1408" data-mp="2qx8qgbcmwp8">Original Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1409" data-mp="3tx87g5svzlde">{selected.resubmitReason || selected.makerReason}</Typography>
                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1410" data-mp="6c0idlhfpg2v">Checker's Rejection Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1411" data-mp="3sh448xhz2fdi">{selected.checkerComment}</Typography>
                </Paper>
              </>}
            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Deleting'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'deleting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1412" data-mp="1b3078wfcm1d" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1413" data-mp="k8ndrjtqny6o">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1414" data-mp="k7ccprroh4j9">Cancel</Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1416" data-mp="38tmn1nw3hm4e" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1417" data-mp="3424of6hjnxml" />} data-id="element-1415" data-mp="17www2brqu3t2">
            {actionType === 'resubmit' ? 'Resubmit' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerHolidayCalendarTable;